'use client'

import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import type { Page } from '@/payload-types'

import { CMSLink } from '@/components/Link'
import { Media } from '@/components/Media'
import { useHeaderTheme } from '@/providers/HeaderTheme'
import { cn } from '@/utilities/ui'

export const DEFAULT_SLIDER_AUTOPLAY_DELAY = 7000

type ImageSliderHeroProps = Page['hero']

export const ImageSliderHero: React.FC<ImageSliderHeroProps> = ({
  autoplay = true,
  autoplayDelay = 7,
  slides,
}) => {
  const { setHeaderTheme } = useHeaderTheme()
  const [activeIndex, setActiveIndex] = useState(0)
  const [hasFocusWithin, setHasFocusWithin] = useState(false)
  const [isDocumentVisible, setIsDocumentVisible] = useState(
    () => typeof document === 'undefined' || !document.hidden,
  )
  const [isHovered, setIsHovered] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  )
  const pointerStartX = useRef<number | null>(null)

  const slideCount = slides?.length || 0
  const displayedIndex = activeIndex % Math.max(slideCount, 1)
  const delay = Math.min(12, Math.max(4, autoplayDelay || 7)) * 1000
  const isAutoPlaying =
    autoplay &&
    slideCount > 1 &&
    !hasFocusWithin &&
    !isHovered &&
    !isPaused &&
    !prefersReducedMotion &&
    isDocumentVisible

  const showSlide = useCallback(
    (index: number) => {
      if (slideCount === 0) return

      setActiveIndex((index + slideCount) % slideCount)
    },
    [slideCount],
  )

  const showNextSlide = useCallback(() => {
    setActiveIndex((currentIndex) => (currentIndex + 1) % Math.max(slideCount, 1))
  }, [slideCount])

  const showPreviousSlide = useCallback(() => {
    setActiveIndex(
      (currentIndex) => (currentIndex - 1 + Math.max(slideCount, 1)) % Math.max(slideCount, 1),
    )
  }, [slideCount])

  useEffect(() => {
    setHeaderTheme('dark')
  }, [setHeaderTheme])

  useEffect(() => {
    const updateDocumentVisibility = () => setIsDocumentVisible(!document.hidden)

    document.addEventListener('visibilitychange', updateDocumentVisibility)

    return () => document.removeEventListener('visibilitychange', updateDocumentVisibility)
  }, [])

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const updateMotionPreference = (event: MediaQueryListEvent) =>
      setPrefersReducedMotion(event.matches)

    mediaQuery.addEventListener?.('change', updateMotionPreference)

    return () => mediaQuery.removeEventListener?.('change', updateMotionPreference)
  }, [])

  useEffect(() => {
    if (!isAutoPlaying) return

    const interval = window.setInterval(showNextSlide, delay)

    return () => window.clearInterval(interval)
  }, [delay, isAutoPlaying, showNextSlide])

  if (!slides || slideCount === 0) return null

  const activeSlide = slides[displayedIndex] || slides[0]

  return (
    <section
      aria-label="Featured content"
      aria-roledescription="carousel"
      className="relative -mt-[10.4rem] min-h-[clamp(38rem,82vh,56rem)] overflow-hidden text-white"
      data-theme="dark"
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setHasFocusWithin(false)
      }}
      onFocusCapture={() => setHasFocusWithin(true)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          showPreviousSlide()
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault()
          showNextSlide()
        }

        if (event.key === 'Home') {
          event.preventDefault()
          showSlide(0)
        }

        if (event.key === 'End') {
          event.preventDefault()
          showSlide(slideCount - 1)
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onPointerDown={(event) => {
        if (event.pointerType !== 'mouse') pointerStartX.current = event.clientX
      }}
      onPointerCancel={() => {
        pointerStartX.current = null
      }}
      onPointerUp={(event) => {
        if (pointerStartX.current === null) return

        const distance = event.clientX - pointerStartX.current
        pointerStartX.current = null

        if (Math.abs(distance) < 50) return

        if (distance > 0) showPreviousSlide()
        else showNextSlide()
      }}
      role="region"
    >
      <div className="absolute inset-0 bg-brand-black">
        {slides.map((slide, index) => {
          const isActive = index === displayedIndex

          return (
            <div
              aria-hidden={!isActive}
              aria-label={`${index + 1} of ${slideCount}`}
              aria-roledescription="slide"
              className={cn(
                'absolute inset-0 transition-opacity duration-700 motion-reduce:transition-none',
                isActive ? 'opacity-100' : 'opacity-0',
              )}
              key={slide.id || `${slide.heading}-${index}`}
              role="group"
            >
              {slide.image && typeof slide.image === 'object' && (
                <Media
                  className="absolute inset-0"
                  fill
                  imgClassName="object-cover"
                  pictureClassName="relative block h-full w-full"
                  priority={index === 0}
                  resource={slide.image}
                  size="100vw"
                />
              )}
            </div>
          )
        })}
        <div className="absolute inset-0 bg-linear-to-r from-brand-black/80 via-brand-black/45 to-brand-black/15" />
        <div className="absolute inset-0 bg-linear-to-t from-brand-black/55 via-transparent to-brand-black/20" />
      </div>

      <div className="container relative z-10 flex min-h-[clamp(38rem,82vh,56rem)] items-end pb-24 pt-48 md:items-center md:pb-20 md:pt-44">
        <div
          aria-atomic="true"
          aria-live={isAutoPlaying ? 'off' : 'polite'}
          className="max-w-3xl"
          key={activeSlide.id || `${activeSlide.heading}-${displayedIndex}`}
        >
          {activeSlide.eyebrow && (
            <p className="mb-5 text-sm font-semibold uppercase tracking-[0.24em] text-white/85">
              {activeSlide.eyebrow}
            </p>
          )}
          <h1 className="max-w-3xl text-5xl font-semibold leading-[1.04] tracking-[-0.04em] md:text-7xl">
            {activeSlide.heading}
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-white/85 md:text-xl">
            {activeSlide.description}
          </p>
          {Array.isArray(activeSlide.links) && activeSlide.links.length > 0 && (
            <ul className="mt-9 flex flex-wrap gap-4">
              {activeSlide.links.map(({ id, link }, index) => (
                <li key={id || index}>
                  <CMSLink {...link} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {slideCount > 1 && (
        <div className="absolute inset-x-0 bottom-7 z-20">
          <div className="container flex items-center justify-between gap-5">
            <div aria-label="Choose a slide" className="flex items-center gap-2" role="group">
              {slides.map((slide, index) => (
                <button
                  aria-current={index === displayedIndex ? 'true' : undefined}
                  aria-label={`Show slide ${index + 1}: ${slide.heading}`}
                  className={cn(
                    'h-2.5 rounded-full border border-white/70 transition-[width,background-color] motion-reduce:transition-none',
                    index === displayedIndex
                      ? 'w-9 bg-white'
                      : 'w-2.5 bg-white/20 hover:bg-white/60',
                  )}
                  key={slide.id || index}
                  onClick={() => showSlide(index)}
                  type="button"
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              {autoplay && !prefersReducedMotion && (
                <button
                  aria-label={
                    isPaused ? 'Resume automatic slide rotation' : 'Pause automatic slide rotation'
                  }
                  className="flex size-11 items-center justify-center rounded-full border border-white/55 bg-brand-black/25 backdrop-blur transition-colors hover:bg-white hover:text-black"
                  onClick={() => setIsPaused((currentValue) => !currentValue)}
                  type="button"
                >
                  {isPaused ? (
                    <Play aria-hidden="true" size={18} />
                  ) : (
                    <Pause aria-hidden="true" size={18} />
                  )}
                </button>
              )}
              <button
                aria-label="Show previous slide"
                className="flex size-11 items-center justify-center rounded-full border border-white/55 bg-brand-black/25 backdrop-blur transition-colors hover:bg-white hover:text-black"
                onClick={showPreviousSlide}
                type="button"
              >
                <ChevronLeft aria-hidden="true" size={20} />
              </button>
              <button
                aria-label="Show next slide"
                className="flex size-11 items-center justify-center rounded-full border border-white/55 bg-brand-black/25 backdrop-blur transition-colors hover:bg-white hover:text-black"
                onClick={showNextSlide}
                type="button"
              >
                <ChevronRight aria-hidden="true" size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
